import React, { useCallback, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    headerTitle,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    getMappingOverviewAction,
} from "../../store/actions";
import MappingOverviewView from "../../store/views/MappingOverview/MappingOverview";

const MappingOverview = (props) => {
    const dispatch = useDispatch();
    const dispatchGetMappingOverview = useCallback(
        (projectId, projectModules) => dispatch(getMappingOverviewAction(projectId, projectModules)),
        [dispatch],
    );

    const {
        modules,
        route,
        location,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
    } = useControllerInitialization(props);

    let { fiscalYear, importId } = matchParams;

    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: MappingOverview,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const { loading = true, success = false } = queryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "MappingOverview");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: MappingOverview,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
        repositoryStatus: { status: 1 },
    };

    const { configSets, loading: profilesLoading } = props.mappingOverview || {};

    const sourceModuleNames = (modules || [])
        .filter((m) => m.name?.startsWith("CSV_"))
        .map((m) => m.name);

    useEffect(() => {
        if (!projectId) return;
        dispatchGetMappingOverview(projectId, sourceModuleNames);
        // Refetch when returning to this route after staging corrections.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, location?.key]);

    if (loading) {
        return LoadingComponent;
    }

    return (
        <GlobalPageLayout
            globalLayoutProps={globalLayoutProps}
            modules={modules}
            headerProps={{
                fiscalYears: undefined,
                importIds: undefined,
                downloadableQueries,
                title: title ? i18n`${title}` : i18n`Mapping Overview`,
                headerComponents,
            }}
            headerComponentsMapper={() => () => null}
            fetch={props.fetch}
            route={route}
            matchParams={matchParams}
            menu={props.menu}
            isLoadingContent={() => false}
        >
            <MappingOverviewView
                configSets={configSets || []}
                i18n={i18n}
                loading={profilesLoading}
                locale={locale}
                projectId={projectId}
                fiscalYear={fiscalYear}
                importId={importId}
                history={history}
            />
        </GlobalPageLayout>
    );
};

MappingOverview.queries = () => [
    {
        resource: "menu_organizations_projects",
        body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
        forceRefresh: true,
    },
    {
        resource: "organization_projects_by_projectId",
        args: { projectId: "" },
        body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
        forceRefresh: true,
    },
];

export default connect(
    ({ user, fetch, menu, mappingOverview }) => ({ user, fetch, menu, mappingOverview }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
    },
)(withRouter(MappingOverview));
